// ================= THE STORE, FROM THE CLIENT'S SIDE =================
//
// Two calls: what do I own and what can I spend (`state`), and buy this card (`buy`). It is the
// shape js/deck-sync.js already established for the accounts API and for the same reasons —
// a thin module that REPORTS what it finds and decides nothing, so the screens above it own the
// display rules and this owns the round trip.
//
// THREE THINGS IT DELIBERATELY DOES NOT DO:
//
//   IT KEEPS NO STATE. There is no cached owned-set here, no balance, nothing a second screen
//   could read after this one went stale. Every caller holds its own answer and asks again when it
//   needs a fresh one. A module-level cache is how a builder comes to believe in a card the player
//   refunded on another tab.
//
//   IT NEVER SHORT-CIRCUITS ON `file://`. DeckSync's header explains this at length and the
//   argument is identical: a hard protocol gate makes the whole round trip untestable, because
//   every jsdom window in this repo is on file://. The fetch simply fails there and is caught,
//   which reaches the same answer honestly. `offline()` is exported for callers that want to skip
//   the attempt, exactly as DeckSync does.
//
//   IT DOES NOT DECIDE WHAT A CARD COSTS. `state().catalog` is the SERVER'S catalog whenever there
//   is a server; window.STORE_CATALOG is the fallback, and its own header says why that copy can
//   only ever be a label. The number charged is decided inside the purchase INSERT and nowhere else.
window.StoreClient = (function () {
  'use strict';

  const MIRROR = () => window.STORE_CATALOG || null;

  function offline() { return window.location && window.location.protocol === 'file:'; }

  // CONTENT-TYPE, NOT STATUS — the same check DeckSync makes, against the same failure. A Pages
  // deploy that lost its Functions answers /api/store with the site's own index.html, and before
  // 404.html shipped it did so with status 200. `res.ok` is true there and `res.json()` throws a
  // parse error that reads like a server fault.
  async function getJson(url, opts) {
    const res = await window.fetch(url, Object.assign({ credentials: 'same-origin' }, opts || {}));
    const type = res.headers.get('Content-Type') || '';
    if (!/json/i.test(type)) throw new Error('api_unavailable');
    return { status: res.status, body: await res.json() };
  }

  // What a caller gets when there is no server to ask: no account, nothing owned, nothing spendable
  // — and the season's catalog anyway, from the client mirror, so a `file://` copy still knows what
  // the season CONTAINS even though it can never sell any of it. The screens use `configured` to
  // decide whether to offer a store at all; they do not have to also test whether the catalog came
  // back, which is one fewer way to render a shop with no prices in it.
  function unreachable() {
    const m = MIRROR();
    return {
      ok: false, configured: false, signedIn: false, offline: true,
      owned: [], balance: 0, earned: 0, spent: 0, streak: 0, remaining: 0,
      season: m ? m.SEASON_ID : null,
      price: m ? m.SEASONAL_CARD_PRICE : 0,
      catalog: m ? m.catalog() : []
    };
  }

  // -> { ok, configured, signedIn, owned, balance, catalog, price, season, ... }
  //
  // NEVER THROWS AND NEVER REJECTS. The library screen calls this during boot; a rejected promise
  // there is a page that renders half a collection. Every failure — no network, no Functions, a
  // 500, HTML where JSON should be — comes back as the unreachable shape above, which every screen
  // already has to handle because it is also what `file://` looks like.
  async function state() {
    let res;
    try { res = await getJson('/api/store'); } catch (e) { return unreachable(); }
    if (res.status !== 200 || !res.body || typeof res.body !== 'object') return unreachable();
    const b = res.body;
    return {
      ok: true,
      configured: !!b.configured,
      signedIn: !!b.signedIn,
      offline: false,
      owned: Array.isArray(b.owned) ? b.owned.slice() : [],
      balance: Number(b.balance) || 0,
      earned: Number(b.earned) || 0,
      spent: Number(b.spent) || 0,
      streak: Number(b.streak) || 0,
      remaining: Number(b.remaining) || 0,
      season: b.season || null,
      price: Number(b.price) || 0,
      catalog: Array.isArray(b.catalog) ? b.catalog.slice() : []
    };
  }

  // Buy one card. -> { ok, bought, already, insufficient, error?, ...the whole new standing }
  //
  // THE ANSWER CARRIES THE WHOLE STANDING, not just a verdict, and that is the point: the caller
  // repaints from `owned` and `balance` in this reply rather than from the fact that a click
  // happened. See the note above applyState in js/library-ui.js — the same function applies this
  // and applies the page-load read, so a purchase cannot paint a screen a reload would disagree
  // with.
  //
  // `bought: false` IS NOT AN ERROR. A duplicate (`already`) and a shortfall (`insufficient`) are
  // both ordinary answers with `ok: true`; the server distinguishes them by asking the primary key
  // rather than by inferring from the balance, because a player who owns the card AND cannot afford
  // another would otherwise be told the wrong one.
  async function buy(cardId) {
    let res;
    try {
      res = await getJson('/api/store/purchase', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // The card and nothing else. A posted price is not read by the endpoint — see its header —
        // and sending one anyway would be this file implying otherwise.
        body: JSON.stringify({ cardId: cardId })
      });
    } catch (e) { return { ok: false, error: 'api_unavailable' }; }

    if (res.status === 401) return { ok: false, error: 'not_signed_in' };
    if (res.status === 404) return { ok: false, error: 'not_for_sale' };
    if (res.status === 503) return { ok: false, error: 'not_configured' };
    if (res.status !== 200) return { ok: false, error: 'server_error' };

    const b = res.body || {};
    return {
      ok: true,
      bought: !!b.bought,
      already: !!b.already,
      insufficient: !!b.insufficient,
      cardId: b.cardId || cardId,
      price: Number(b.price) || 0,
      owned: Array.isArray(b.owned) ? b.owned.slice() : [],
      balance: Number(b.balance) || 0,
      earned: Number(b.earned) || 0,
      spent: Number(b.spent) || 0,
      streak: Number(b.streak) || 0,
      remaining: Number(b.remaining) || 0
    };
  }

  return {
    offline: offline,
    state: state,
    buy: buy,
    // Exported for the tests and for anything that needs the "no server" shape without making a
    // request to discover it.
    unreachable: unreachable
  };
})();
